package top.wcpe.allincore.ui.runtime.canvas

/**
 * DrawBlob 极小读取器：替代 `java.nio.ByteBuffer`（FR-UI-021 批F，大端序与 ByteBuffer 默认一致）。
 *
 * 只提供 canvas 簇消费的三个能力（单字节 / 大端 short / 位置读写）；
 * 越界抛 [IndexOutOfBoundsException]（对应迁移前 BufferUnderflowException 的捕获点）。
 */
internal class BlobReader(private val data: ByteArray) {

    private var pos = 0

    fun position(): Int = pos

    fun position(newPosition: Int) {
        if (newPosition < 0 || newPosition > data.size) {
            throw IndexOutOfBoundsException("position=$newPosition 超出 [0, ${data.size}]")
        }
        pos = newPosition
    }

    fun get(): Byte {
        if (pos >= data.size) throw IndexOutOfBoundsException("读越界：pos=$pos size=${data.size}")
        return data[pos++]
    }

    /** 绝对下标读单字节（peek，不前进），语义同 ByteBuffer.get(index)。 */
    fun get(index: Int): Byte {
        if (index < 0 || index >= data.size) throw IndexOutOfBoundsException("读越界：index=$index size=${data.size}")
        return data[index]
    }

    /** 批量读满 [dest]，语义同 ByteBuffer.get(byte[])。 */
    fun get(dest: ByteArray) {
        if (pos + dest.size >
            data.size
        ) {
            throw IndexOutOfBoundsException("读越界：pos=$pos need=${dest.size} size=${data.size}")
        }
        data.copyInto(dest, 0, pos, pos + dest.size)
        pos += dest.size
    }

    /** 大端 16 位（属性形态 `buf.short`，语义同 ByteBuffer.getShort()）。 */
    val short: Short
        get() {
            if (pos + 1 >= data.size) throw IndexOutOfBoundsException("读越界：pos=$pos size=${data.size}")
            val hi = data[pos++].toInt() and 0xFF
            val lo = data[pos++].toInt() and 0xFF
            return ((hi shl 8) or lo).toShort()
        }

    /** 大端 64 位 IEEE-754 double，语义同 ByteBuffer.getDouble()。 */
    fun getDouble(): Double {
        var bits = 0L
        repeat(8) {
            if (pos >= data.size) throw IndexOutOfBoundsException("读越界：pos=$pos size=${data.size}")
            bits = (bits shl 8) or (data[pos++].toLong() and 0xFF)
        }
        return Double.fromBits(bits)
    }
}
